'use client';

import React from 'react';
import { decodeHtmlEntities } from '@/lib/utils';

/**
 * 基于 next/image API 的图片组件（Vite/React Router 下替代 next/image）
 * 支持 width/height 与 fill 两种布局，无服务端优化，直接渲染原生 img
 *
 * 内置 HTML 实体解码：后端 XSS 过滤会把 URL 里合法的 & 转义成 &amp;，
 * 这里统一 decode，让所有 CoImage 消费点无需手动处理就能正确加载带 query 参数的 URL
 */
export interface CoImageProps
  extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'src' | 'alt' | 'width' | 'height'> {
  /** 图片地址，或 Vite/静态 import 得到的 { src: string } */
  src: string | { src: string };
  alt: string;
  width?: number;
  height?: number;
  /** 填充父容器，等价于 next/image 的 fill */
  fill?: boolean;
  /** 兼容 next/image，此处不生效 */
  sizes?: string;
  /** 兼容 next/image，此处不生效 */
  priority?: boolean;
  /** 兼容 next/image，此处不生效 */
  loading?: 'lazy' | 'eager';
  /** 兼容 next/image，此处不生效 */
  quality?: number;
  /** 兼容 next/image，此处不生效 */
  placeholder?: 'blur' | 'empty';
  /** 兼容 next/image，此处不生效 */
  blurDataURL?: string;
}

function getSrc(src: string | { src: string }): string {
  const raw = typeof src === 'string' ? src : src?.src || '';
  return decodeHtmlEntities(raw);
}

export function CoImage({
  src,
  alt,
  width,
  height,
  fill = false,
  className = '',
  style,
  sizes,
  priority,
  loading,
  quality,
  placeholder,
  blurDataURL,
  onLoad,
  onError,
  ...rest
}: CoImageProps) {
  const resolvedSrc = getSrc(src);

  if (fill) {
    return (
      <img
        src={resolvedSrc}
        alt={alt}
        className={`absolute inset-0 object-cover w-full h-full ${className}`.trim()}
        style={{
          ...style,
          width: width,
          height: height,
        }}
        loading={loading ?? 'lazy'}
        onLoad={onLoad}
        onError={onError}
        {...rest}
      />
    );
  }

  return (
    <img
      src={resolvedSrc}
      alt={alt}
      width={width}
      height={height}
      className={className}
      style={{
        ...style,
        width: width,
        height: height,
      }}
      loading={loading ?? 'lazy'}
      onLoad={onLoad}
      onError={onError}
      {...rest}
    />
  );
}

export default CoImage;
